import type { AutomationTemplateContents } from '@reach/shared-types/automation-template';
import { BusinessAutomationActionTypeEnum, TimezoneEnum } from '@reach/shared-types/business-automation';

const {
  SEND_COMMUNICATION,
  WAIT_FOR_DURATION,
  WAIT_UNTIL_AT_LEAST_NEXT_AVAILABLE_TIME,
  WAIT_UNTIL_NEXT_OCCURRENCE,
  CHECK_SEGMENT_MEMBERSHIP,
} = BusinessAutomationActionTypeEnum;

// Default action data objects (without type, templateActionId, or index)
const sendCommunicationDefaults = {
  html: '',
  css: '',
  compiledHtml: '',
  text: '',
  subject: '',
  previewText: '',
};

const waitForDurationDefaults = {
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0,
};

const waitUntilDefaults: {
  hour?: number;
  minute?: number;
  timezone: TimezoneEnum;
} = {
  timezone: TimezoneEnum.EST,
};

const checkSegmentMembershipDefaults = {
  segmentIds: [],
};

// Lookup object for action data without type
// Using Partial to allow undefined values for fields that need configuration
export const actionTypeDefaults: Record<
  string,
  Partial<Omit<AutomationTemplateContents['actionData'][0], 'type' | 'templateActionId' | 'index'>>
> = {
  [SEND_COMMUNICATION]: sendCommunicationDefaults,
  [WAIT_FOR_DURATION]: waitForDurationDefaults,
  [WAIT_UNTIL_AT_LEAST_NEXT_AVAILABLE_TIME]: waitUntilDefaults,
  [WAIT_UNTIL_NEXT_OCCURRENCE]: waitUntilDefaults,
  [CHECK_SEGMENT_MEMBERSHIP]: checkSegmentMembershipDefaults,
};

// Type helpers for narrowing action types
type SendCommunicationAction = Extract<
  AutomationTemplateContents['actionData'][0],
  { type: typeof SEND_COMMUNICATION }
>;

type WaitForDurationAction = Extract<
  AutomationTemplateContents['actionData'][0],
  { type: typeof WAIT_FOR_DURATION }
>;

type WaitUntilAction = Extract<
  AutomationTemplateContents['actionData'][0],
  { type: typeof WAIT_UNTIL_AT_LEAST_NEXT_AVAILABLE_TIME } | { type: typeof WAIT_UNTIL_NEXT_OCCURRENCE }
>;

type CheckSegmentMembershipAction = Extract<
  AutomationTemplateContents['actionData'][0],
  { type: typeof CHECK_SEGMENT_MEMBERSHIP }
>;

// Functions to check if an action needs setup based on its type
const checkSendCommunicationNeedsSetup = (
  action: AutomationTemplateContents['actionData'][0],
): boolean => {
  const sendAction = action as SendCommunicationAction;
  return !(sendAction.subject || sendAction.text || sendAction.html);
};

const checkWaitForDurationNeedsSetup = (
  action: AutomationTemplateContents['actionData'][0],
): boolean => {
  const waitAction = action as WaitForDurationAction;
  const { days = 0, hours = 0, minutes = 0, seconds = 0 } = waitAction;
  return days === 0 && hours === 0 && minutes === 0 && seconds === 0;
};

const checkWaitUntilNeedsSetup = (action: AutomationTemplateContents['actionData'][0]): boolean => {
  const waitUntilAction = action as WaitUntilAction;
  return waitUntilAction.hour === undefined || waitUntilAction.minute === undefined;
};

const checkSegmentMembershipNeedsSetup = (
  action: AutomationTemplateContents['actionData'][0],
): boolean => {
  const segmentAction = action as CheckSegmentMembershipAction;
  return !segmentAction.segmentIds || segmentAction.segmentIds.length === 0;
};

// Lookup table for checking if actions need setup
const checkActionNeedsSetupLookup: Record<
  string,
  (action: AutomationTemplateContents['actionData'][0]) => boolean
> = {
  [SEND_COMMUNICATION]: checkSendCommunicationNeedsSetup,
  [WAIT_FOR_DURATION]: checkWaitForDurationNeedsSetup,
  [WAIT_UNTIL_AT_LEAST_NEXT_AVAILABLE_TIME]: checkWaitUntilNeedsSetup,
  [WAIT_UNTIL_NEXT_OCCURRENCE]: checkWaitUntilNeedsSetup,
  [CHECK_SEGMENT_MEMBERSHIP]: checkSegmentMembershipNeedsSetup,
};

export function checkActionNeedsSetup(action: AutomationTemplateContents['actionData'][0]): boolean {
  const checker = checkActionNeedsSetupLookup[action.type];
  return checker ? checker(action) : false;
}
